import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@services/notification.service';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { TransferErrorPopupComponent } from '@shared/components/transfer-error-popup/transfer-error-popup.component';
import { aciesDateFormat } from '@shared/helpers';
import { GenericTableColumn } from '@shared/models/generictablecolumn';
import { UserToNamePipe } from '@shared/pipes/user-to-name.pipe';
import { CuttingService, IBasket, IResponseBasket } from '@shared/services/cutting.service';
import { BasketTransferService } from '@shared/services/export-import-services/basket';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { catchError, finalize, map, Subject, takeUntil, tap, throwError } from 'rxjs';
import { CreateUpdateBasketDialogComponent } from './create-update-basket/create-update-basket';

@Component({
	selector: 'app-baskets',
	templateUrl: './baskets.component.html',
	styleUrls: ['./baskets.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class BasketsComponent implements OnInit {
	private destroy$: Subject<void> = new Subject<void>();
	public isLoading: boolean = false;
	public showSearch: boolean = false;
	public tableSource = new MatTableDataSource<IBasket>();
	public basketsFilter: string = '';
	public pageSizes = [10, 20, 50, 100];
	public initialPageSize = 20;
	public selected: object[] = [];
	public unsubscribe: Subject<void> = new Subject<void>();
	public loadingSubject: Subject<boolean> = new Subject<boolean>();
	public countSubject$: Subject<number> = new Subject<number>();
	public count$ = this.countSubject$.asObservable();

	public registerTableColumns: GenericTableColumn[] = [
		{ name: 'masterdata.cutting.name', dataKey: 'name' },
		{ name: 'masterdata.cutting.description', dataKey: 'description' },
		{ name: 'masterdata.cutting.capacity', dataKey: 'capacity' },
		{ name: 'masterdata.cutting.updatedDate', dataKey: 'updatedDate', sortKey: 'updatedAtEpoch' },
		{
			name: 'masterdata.cutting.updatedBy',
			dataKey: 'updatedBy',
			pipe: UserToNamePipe,
			sortAsShown: true,
		},
	];

	public regRowActions: object[] = [
		{
			title: 'masterdata.cutting.actions.edit',
			handler: 'editMasterdataRow',
		},
		{
			title: 'masterdata.cutting.actions.delete',
			handler: 'deleteMasterdataRow',
		},
	];

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'masterdata.cutting.newbtn.baskets',
			click: () => this.openCreateUpdateBasket(),
		},
	];

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.cutting.basketImportExport.import.button',
			click: () => this.onImport(),
			permissions: () => ['cutting:BasketPost', 'cutting:BasketPut', 'cutting:BasketList'],
		},
		{
			icon: () => 'acies_upload',
			text: () => 'masterdata.cutting.basketImportExport.export.button',
			click: () => this.onExport(),
			permissions: () => ['cutting:BasketList'],
		},
		{
			icon: () => 'acies_template',
			text: () => 'masterdata.cutting.basketImportExport.import.template.button',
			click: () => this.onDownloadTemplate(),
		},
	];

	constructor(
		public dialog: MatDialog,
		private translator: TranslateService,
		private cuttingService: CuttingService,
		private notificationService: NotificationService,
		private basketTransferService: BasketTransferService,
		private importValidatorService: ImportValidatorService,
	) {}

	ngOnDestroy() {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	ngOnInit(): void {
		this.isLoading = true;
		this.loadData();
	}

	loadData(): void {
		this.cuttingService
			.getBaskets()
			.pipe(
				tap((data) => {
					this.isLoading = true;
					this.countSubject$.next(data.items.length);
				}),
				map((data) => {
					const basket$ = data.items.map((basket) => {
						return {
							self: basket.self,
							name: basket.name,
							description: basket.description,
							capacity: basket.capacity.maximum,
							updatedDate: aciesDateFormat(new Date(basket.meta.updatedAt), true, false),
							updatedAtEpoch: basket.meta.updatedAt,
							updatedBy: this.cuttingService.getLastSegment(basket.meta.updatedBy.self),
						};
					});
					return basket$;
				}),
				finalize(() => {
					this.isLoading = false;
				}),
			)
			.subscribe((baskets$) => {
				this.tableSource.data = baskets$;
			});
	}

	handleSelected(listOfSelected: object[]): void {
		this.selected = listOfSelected;
	}

	public onDownloadTemplate(): void {
		this.basketTransferService.downloadTemplate();
		this.notificationService.notify(['masterdata.cutting.basketImportExport.import.template.notification.header'], ['masterdata.cutting.basketImportExport.import.template.notification.content'], 'success');
	}

	public onExport(): void {
		this.basketTransferService.exportData(this.tableSource.data).subscribe({
			next: () => {
				this.notificationService.notify(['masterdata.cutting.basketImportExport.export.notification.success.header'], ['masterdata.cutting.basketImportExport.export.notification.success.content'], 'success');
			},
			error: (error: Error) => {
				console.error('Failed to export baskets', error);
				this.notificationService.notify(['masterdata.cutting.basketImportExport.export.notification.error.header'], ['masterdata.cutting.basketImportExport.export.notification.error.content'], 'error');
				this.dialog.open(TransferErrorPopupComponent, {
					width: '450px',
					data: { message: error.message },
				});
			},
		});
	}

	public onImport(): void {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'masterdata.cutting.basketImportExport.import.title',
				subHeadingKey: 'masterdata.cutting.basketImportExport.import.subtitle',
				confirmButtonKey: 'masterdata.cutting.basketImportExport.import.button',
				notificationHeader: 'masterdata.cutting.basketImportExport.import.notification.success.header',
				notificationContent: 'masterdata.cutting.basketImportExport.import.notification.success.content',
				notificationErrorHeader: 'masterdata.cutting.basketImportExport.import.notification.error.header',
				notificationErrorContent: 'masterdata.cutting.basketImportExport.import.notification.error.content',
				notificationClientErrorHeader: 'masterdata.cutting.basketImportExport.import.notification.clientError.header',
				notificationClientErrorContent: 'masterdata.cutting.basketImportExport.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.importValidatorService,
				data: this.basketTransferService,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.loadData();
			});
	}

	handleRowMenuAction(data: object): void {
		switch (data['action'].handler) {
			case 'editMasterdataRow':
				this.openCreateUpdateBasket(data['data']);
				break;
			case 'deleteMasterdataRow':
				this.onDeleteBasket(data['data']);
				break;
			default:
				break;
		}
	}

	handleReloadData(_: object): void {
		// Empty function to enable client side only pagination
		// this.loadData(data['pageSize'] * data['pageNumber']);
	}

	openCreateUpdateBasket(setupData: object = null) {
		const dialogRef = this.dialog.open(CreateUpdateBasketDialogComponent, {
			disableClose: true,
			width: '650px',
			data: setupData,
		});
		dialogRef.componentInstance.saved.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.loadData();
		});
	}

	private handleError(error: HttpErrorResponse) {
		if (error.status === 0) {
			// A client-side or network error occurred. Handle it accordingly.
			console.error('An error occurred:', error.error);
		} else {
			// The backend returned an unsuccessful response code.
			// The response body may contain clues as to what went wrong.
			console.error(`Backend returned code ${error.status}, body was: `, error.error);
		}
		// Return an observable with a user-facing error message.
		return throwError(() => new Error('Something bad happened; please try again later.'));
	}

	onDeleteBasket(basket: IResponseBasket) {
		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('masterdata.cutting.delete.baskets.heading'),
				content: this.translator.instant('masterdata.cutting.delete.baskets.content', {
					name: basket.name,
				}),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				if (result) {
					this.cuttingService
						.delete(basket.self)
						.pipe(catchError(this.handleError))
						.subscribe(() => {
							this.notificationService.notify(['masterdata.cutting.delete.baskets.notification.header'], ['masterdata.cutting.delete.baskets.notification.content', { name: basket.name }], 'success');
							this.loadData();
						});
				}
			});
	}
}
